import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDiagnosisListByStatusQuery } from 'api/Queries/Diagnosis/useDiagnosisListByStatusQuery';
import { AccordionList } from 'components/Accordion/AccordionList';
import { useLoginStore } from 'store/useLoginDataStore';
import { addNoShowDisabledField } from 'pages/Admin/MindUp/utils/mindUpAdminFunctions';
import { IDDiagnosis } from 'models/DCK/basicInterface';
import 'moment/locale/ko'; // 한국어 로케일 설정
import { BorderColor } from 'styles/Global/colorSemantic';
import { Complete, NoShowTerminate } from 'components/CounselingCards/CounselingCards';
import useToggle from 'hooks/CustomHooks/useToggle';
import { formatDateDay } from 'utils/calculateTime';
import { getDiagnosisStatus } from 'pages/DCK/utils';
import MindUpAdminMobileMenu from 'components/Menu/MindUpAdminMobileMenu';
import MindUpAdminMobileCompletedUserCounselHeader from './MindUpAdminMobileCompletedUserCounselHeader';
import { NothingDataInMindUpAdminMobileCompletedNoShow, NothingDataInMindUpAdminMobileCompletedUserCounselPage } from 'pages/Admin/DCK/AddDiagnosisPage/NothingDataInPagination';
import { resetScrollPosition } from 'components/Common/ScrollToTop';
import _ from 'lodash';

// 그룹화된 진료 데이터를 위한 인터페이스
interface IGroupedDiagnosisItem {
    dateTimeDisplay: string;
    items: IDDiagnosis[];
    visible?: boolean;
}

const MindUpAdminMobileCompletedUserCounselPage = () => {
    // const [initData, setInitData] = useState<IDDiagnosis[]>();
    const [groupedDiagnosis, setGroupedDiagnosis] = useState<IGroupedDiagnosisItem[]>([]);
    const [isSwiping, setIsSwiping] = useState(false);

    // 0: 전체, 1: 요청 전, 2: 예약 대기
    const [menu, setMenu] = useState<number>(0);

    const [onMenu, setOnMenu] = useToggle();

    const { data: diagnosisListData, isLoading: diagnosisListLoading, refetch: refetchDiagnosisListData } = useDiagnosisListByStatusQuery({ status: [5] }, menu);

    const { loginData } = useLoginStore();

    const touchStartRef = useRef(0);
    const touchThreshold = 50; // 스와이프로 인식할 최소 거리(px)

    useEffect(() => {
        resetScrollPosition();
    }, [menu]);

    // Data Fetching
    useEffect(() => {
        if (!diagnosisListLoading && diagnosisListData) {
            processDiagnosisData();
        }
    }, [diagnosisListData]);

    const processDiagnosisData = () => {
        if (!diagnosisListData?.length) return;

        const filteredSorted = getFilteredSortedData(diagnosisListData, loginData.id, menu);

        const grouped = groupByDisplayDate(filteredSorted);

        const groupedArray = sortGroupsDesc(grouped);

        // 첫번째 아코디언 기본값: 펼침
        if (groupedArray.length > 0) groupedArray[0].visible = true;

        setGroupedDiagnosis(groupedArray);
    };

    function getFilteredSortedData(list: IDDiagnosis[], doctorId: string, menu: number) {
        let result = list.filter((d) => d.doctorId === doctorId).sort((a, b) => b.diagnosisDateTime.localeCompare(a.diagnosisDateTime));

        result = addNoShowDisabledField(result);

        if (menu === 1) {
            result = result.filter((d) => getDiagnosisStatus(d) === 'counselComplete');
        } else if (menu === 2) {
            result = result.filter((d) => getDiagnosisStatus(d) === 'noshow');
        }

        return result;
    }

    function groupByDisplayDate(list: IDDiagnosis[]) {
        const map = new Map<string, IGroupedDiagnosisItem>();

        for (const d of list) {
            const key = formatDateDay(d.diagnosisDateTime);
            if (!map.has(key)) {
                map.set(key, { dateTimeDisplay: key, items: [d], visible: false });
            } else {
                map.get(key)!.items.push(d);
            }
        }

        return map;
    }

    function sortGroupsDesc(groupMap: Map<string, IGroupedDiagnosisItem>) {
        const arr = Array.from(groupMap.values());

        // 각 그룹 내부 아이템: 과거순으로 정렬
        for (const g of arr) {
            g.items.sort((a, b) => a.diagnosisDateTime.localeCompare(b.diagnosisDateTime));
        }

        // 그룹 자체: 최신순
        arr.sort((a, b) => {
            const aTop = a.items[0]?.diagnosisDateTime ?? '';
            const bTop = b.items[0]?.diagnosisDateTime ?? '';
            return bTop.localeCompare(aTop);
        });

        return arr;
    }

    // 아코디언 토글 핸들러
    const handleAccordionToggle = (index: number) => {
        setGroupedDiagnosis((prev) =>
            prev.map((group, i) => {
                if (i === index) {
                    return { ...group, visible: !group.visible };
                }
                return group;
            })
        );
    };

    // 터치 시작 감지
    const handleTouchStart = (e: any) => {
        touchStartRef.current = e.touches[0].clientX;
        setIsSwiping(true);
    };

    // 터치 종료 처리
    const handleTouchEnd = (e: any) => {
        if (!isSwiping) return;

        const touchEnd = e.changedTouches[0].clientX;
        const distance = touchEnd - touchStartRef.current;

        // 충분한 거리를 스와이프했을 때만 탭 변경
        if (Math.abs(distance) > touchThreshold) {
            if (distance > 0 && menu > 0) {
                // 오른쪽으로 스와이프 - 이전 탭
                setMenu(menu - 1);
            } else if (distance < 0 && menu < 2) {
                // 탭 개수에 맞게 조정
                // 왼쪽으로 스와이프 - 다음 탭
                setMenu(menu + 1);
            }
        }

        setIsSwiping(false);
    };

    return (
        <>
            {onMenu && <MindUpAdminMobileMenu close={setOnMenu} />}
            <MindUpAdminMobileCompletedUserCounselHeader menu={menu} setMenu={setMenu} onClick={setOnMenu} />
            <Layout>
                <SwipeContainer onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                    {groupedDiagnosis.length === 0 ? (
                        menu !== 2 ? (
                            <NothingDataInMindUpAdminMobileCompletedUserCounselPage />
                        ) : (
                            <NothingDataInMindUpAdminMobileCompletedNoShow />
                        )
                    ) : (
                        groupedDiagnosis.map((group, index) => {
                            return (
                                <AccordionListContainer key={index}>
                                    <AccordionList date={group.dateTimeDisplay} number={group.items.length} onClick={() => handleAccordionToggle(index)} selected={group.visible} />
                                    {group.visible &&
                                        group.items.map((item, itemIndex) =>
                                            getDiagnosisStatus(item) === 'noshow' ? <NoShowTerminate key={itemIndex} diagnosisInfo={item} /> : <Complete key={itemIndex} diagnosisInfo={item} />
                                        )}
                                </AccordionListContainer>
                            );
                        })
                    )}
                    {groupedDiagnosis.length !== 0 && <BottomSpace />}
                </SwipeContainer>
            </Layout>
        </>
    );
};

export default MindUpAdminMobileCompletedUserCounselPage;
const SwipeContainer = styled.div`
    width: 100%;
    height: 100%;
`;

const Layout = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100vh;
    height: auto;
    padding-top: 96px;
`;

const AccordionListContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 14px;
    padding: 19px 16px;
    border-bottom: 1px solid ${BorderColor.color_border_weak};
    &:last-child {
        margin-bottom: 56px;
    }
`;

const BottomSpace = styled.div`
    width: 100%;
    height: 56px;
    flex-shrink: 0;
`;
